import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { setupEmailNotifications } from './services/emailService';
import { AuthProvider } from './contexts/AuthContext';

// Pages
import Home from './pages/Home';
import DemoClass from './pages/DemoClass';
import Careers from './pages/Careers';
import Courses from './pages/Courses';
import About from './pages/About';
import Contact from './pages/Contact';
import Resources from './pages/Resources';
import CurriculumPricing from './pages/CurriculumPricing';
import MakePayment from './pages/MakePayment';
import Testimonials from './pages/Testimonials';
import AdminDashboard from './pages/AdminDashboard';
import AdminLogin from './pages/AdminLogin';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import EmailAnalyticsDashboard from './components/EmailAnalyticsDashboard';

const theme = createTheme({
  palette: {
    primary: {
      main: '#7B2CBF', // WhiteHat Jr's primary purple
      light: '#9D4EDD',
      dark: '#5A189A',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#FF6B6B', // Coral accent color
      light: '#FF8E8E',
      dark: '#E85D5D',
      contrastText: '#ffffff',
    },
    background: {
      default: '#ffffff',
      paper: '#F8F9FA',
    },
    text: {
      primary: '#2D3436',
      secondary: '#636E72',
    },
    error: {
      main: '#FF6B6B',
      light: '#FF8E8E',
      dark: '#E85D5D',
    },
    success: {
      main: '#00B894',
      light: '#55EFC4',
      dark: '#00A187',
    },
    warning: {
      main: '#FDCB6E',
      light: '#FFEAA7',
      dark: '#E5B94D',
    },
    info: {
      main: '#74B9FF',
      light: '#81ECEC',
      dark: '#5FA8E3',
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 24px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(123, 44, 191, 0.2)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
        outlined: {
          borderWidth: 1.5,
        },
      },
    },
  },
});

function App() {
  useEffect(() => {
    // Initialize email notifications
    setupEmailNotifications();
  }, []);

  return (
    <AuthProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <div className="App">
            <Navbar />
            <main>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/demo-class" element={<DemoClass />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/courses" element={<Courses />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/testimonials" element={<Testimonials />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/curriculum-pricing" element={<CurriculumPricing />} />
                <Route path="/make-payment" element={<MakePayment />} />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route path="/admin/email-analytics" element={
                  <ProtectedRoute>
                    <EmailAnalyticsDashboard />
                  </ProtectedRoute>
                } />
              </Routes>
            </main>
            <Footer />
            <ToastContainer position="top-right" autoClose={5000} />
          </div>
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
